<template>
    <div
        class="campaign-stage-container"
        :data-stage-context="isSequence && 'sequence' || 'main'"
        tabindex="1"
        @keyup="handleKeyUp"
        @keydown="handleKeyDown"
    >
        <div class="campaign-stage" :class="{'show-sidebar': showSidebar}">
            <KonvaCampaignSidebar
                v-if="showSidebar"
                data-qa="konva-campaign-sidebar"
                :enabled-integrations="enabledIntegrations"
                :enabled-cell-types="enabledCellTypes"
                :is-sequence="isSequence"
                @toggle-collapse="handleSidebarToggle"
            />
            <!-- TODO: move reporting stuff to its own component -->
            <div
                v-if="showReportingView"
                class="card-filter-container"
            >
                <div class="left-side">
                    <div
                        v-if="displayWidgets"
                        class="reporting-cards"
                    >
                        <ReportingCard
                            :total="uniqueContacts.toString()"
                            :title="$t('funnel.canvas.reporting.unique.contacts.title')"
                            :subtitle="$t('funnel.canvas.reporting.unique.contacts.total')"
                            article-url="https://help.infusionsoft.com/help/campaign-reporting-tab"
                            @open-report="$emit('open-report', 'FunnelUniqueContacts')"
                        />

                        <DsPopover
                            class="reporting-card-popover"
                            data-qa="report-click-to-open"
                            position="right"
                            :is-open="showClickToOpen"
                            @close="closeClickToOpen"
                        >
                            <template #reference>
                                <ReportingCard
                                    :total="clickToOpenRatioEmails"
                                    :title="$t('funnel.canvas.reporting.click.to.open.rate.title')"
                                    :subtitle="$t('funnel.canvas.reporting.click.to.open.rate.subtitle')"
                                    @open-popup="openClickToOpen"
                                    @open-report="$emit('open-report', 'CampaignEmailOpenReport')"
                                />
                            </template>

                            <template #default>
                                <div class="wrapper-popup">
                                    <p class="main-text">
                                        {{ $t('funnel.automation.reporting.ctor.main.label') }}
                                    </p>
                                    <p class="sub-heading">
                                        <strong>{{ $t('funnel.automation.reporting.ctor.sub.heading.label') }}</strong>
                                    </p>
                                    <ul class="calculation-list">
                                        <li>
                                            {{ $t('funnel.automation.reporting.ctor.calculation.list.1') }}
                                        </li>
                                        <li>
                                            {{ $t('funnel.automation.reporting.ctor.calculation.list.2') }}
                                        </li>
                                    </ul>
                                </div>
                            </template>
                        </DsPopover>
                    </div>

                    <KonvaReportingFilterButton
                        :preset="preset"
                        @on-open-filter-modal="$emit('on-open-filter-modal')"
                    />
                </div>

                <EmailPerformanceCard
                    v-if="displayWidgets"
                    :email-stats="emailStats"
                    @open-email-performance-report="(type) => $emit('open-email-performance-report', type)"
                    @open-sequence-email-report="$emit('open-sequence-email-report')"
                />
            </div>
            <div
                ref="container"
                class="stage-dropzone"
                @drop="onDrop"
                @dragover.prevent
                @drop.stop.prevent
                @dragenter.prevent
            >
                <v-stage
                    ref="stage"
                    tabindex="2"
                    outline="none"
                    data-qa="konva-campaign-stage"
                    :config="configContainer"
                    :style="stageStyleOverride"
                    class="campaign-builder-body"
                    :class="{'sequence-canvas': isSequence, 'main-canvas': !isSequence, 'with-grid': shouldShowGrid }"
                    @wheel="handleScroll"
                    @click="onClickStage"
                    @contextmenu="onContextMenu"
                    @mousedown="onMouseDown"
                    @mousemove="onMouseMove"
                    @mouseup="onMouseUp"
                    @dragend="onPanStage"
                    @dragmove="onDrag"
                >
                    <v-layer ref="layer">
                        <v-arrow v-if="configAttachingEdge" :config="configAttachingEdge.arrowConfig" />
                        <KonvaCampaignImage
                            v-if="configAttachingEdge"
                            :src="configAttachingEdge.pointerImage"
                            :config="configAttachingEdge.pointerConfig"
                        />

                        <template v-if="draggedCellIds.length">
                            <v-line v-if="showHorizontalGuideline" :config="configDragGuideline.horizontal" />
                            <v-line v-if="showVerticalGuideline" :config="configDragGuideline.vertical" />
                        </template>

                        <template v-if="holdingShift && configSelectBox.x">
                            <v-rect :config="configSelectBox" />

                            <v-rect
                                v-for="configSelectBoxCorner in configSelectBoxCorners"
                                :key="configSelectBoxCorner.index"
                                :config="configSelectBoxCorner"
                            />
                        </template>

                        <v-group v-for="edge in displayedEdges" :key="edge.element.id">
                            <KonvaCampaignImage
                                :src="edge.pointerImage"
                                :config="edge.pointerConfig"
                            />

                            <v-arrow
                                :config="edge.arrowConfig"
                                @click="(event) => onClickCell({ cell: edge.element, event })"
                                @mouseover="(event) => onMouseEnter({ event, cell: edge.element })"
                                @mouseleave="(event) => onMouseExit({ event, cell: edge.element })"
                            />
                        </v-group>

                        <template v-if="showInlineAdd">
                            <KonvaAutomationInlineAdd
                                v-for="sourceElement in validInlineAddSources"
                                :key="`src-${sourceElement.meta.id}`"
                                :show-big-card="shouldShowBigCard"
                                :is-inline-add-open="isInlineAddOpen"
                                :source-element="sourceElement"
                                @open-inline-add="onOpenInlineAddMenu"
                                @close-inline-add="onCloseInlineAddMenu"
                            />
                        </template>

                        <v-group v-if="showFirstTimeUse" :config="configFirstTimeUseContainer">
                            <v-text :config="configFirstTimeUse" />

                            <v-text :config="configFirstTimeUseSubheader" />

                            <KonvaCampaignImage
                                v-if="showFirstTimeUseImg"
                                :config="configFirstTimeUseImg.config"
                                :src="configFirstTimeUseImg.src"
                            />

                            <KonvaAutomationInlineAdd
                                v-if="bigCardInlineAddEnabled"
                                show-big-card
                                is-first-time-use
                                :is-inline-add-open="isInlineAddOpen"
                                @open-inline-add="onOpenInlineAddMenu"
                                @close-inline-add="onCloseInlineAddMenu"
                            />
                        </v-group>

                        <KonvaCampaignCell
                            v-for="cell in displayCells"
                            :key="cell.meta.id"
                            :selected-cells="selectedCells"
                            :inline-add-sources="validInlineAddSources"
                            :cell="cell"
                            :cells="cells"
                            :ghost-states-enabled="flagsInfo.ghostStatesEnabled"
                            :has-been-published="cellHasBeenPublished(cell)"
                            :selected-sequence="selectedSequence"
                            :should-show-labels="shouldShowLabels"
                            :should-show-big-card="shouldShowBigCard"
                            :should-show-reporting-view="showReportingView"
                            :cell-reporting-data="getCellReportingData(cell)"
                            :is-hovering="!isPanning && hoveredCellId === cell.meta.id"
                            :is-draggable="!isPanning && !showReportingView"
                            :is-dragging="cell.isBeingDragged"
                            :is-copying="isCopying"
                            :is-editing-name="editableNameData && editableNameData.cell.meta.id === cell.meta.id"
                            :is-sequence-view="isSequence"
                            :attaching-edge-from="attachingEdgeFrom"
                            :dragged-edge-valid-state="getCellValidState(cell)"
                            :enabled-integrations="enabledIntegrations"
                            :preset="preset"
                            :flags-info="flagsInfo"
                            :beta-features-enabled="betaFeaturesEnabled"
                            @on-click-name="onClickName"
                            @on-drag-edge="onDragEdge"
                            @on-drop-edge="onDropEdge"
                            @open-between-add="onOpenBetweenAdd"
                            @on-mouse-enter="(event) => onMouseEnter({ cell, event })"
                            @on-mouse-exit="(event) => onMouseExit({ cell, event })"
                            @drag-start="() => onDragStart(cell)"
                            @drag-move="(event) => onDragMove(event, cell)"
                            @drag-end="(event) => dragEnd(event, cell)"
                            @on-click-cell="onClickCell"
                            @on-double-click-cell="(cell) => onDoubleClickCell(cell)"
                        />
                    </v-layer>

                    <v-layer
                        ref="movementLayer"
                    >
                        <KonvaDraggedCell
                            v-for="cell in draggedCells"
                            :key="cell.id"
                            :cell="cell"
                            :should-show-big-card="shouldShowBigCard"
                            :offset="dragOffset"
                        />
                    </v-layer>

                    <v-layer v-if="debugMode">
                        <v-text v-if="hoveredCell && debugMode" :config="configDebugmodeContentHoveredCellText" />
                    </v-layer>
                </v-stage>
            </div>

            <div class="campaign-overlay-footer-buttons" :class="{'collapsed': isSidebarCollapsed}">
                <template v-if="!showReportingView">
                    <DsTooltip v-if="showHotkeys">
                        <template #reference>
                            <DsButton
                                class="overlay-btn"
                                data-qa="keyboard-button"
                                @click="toggleHotkeys"
                            >
                                <img src="/resources/funnel/images/svg/cell-icon/keyboard.svg" />
                            </DsButton>
                        </template>
                        <template #default>
                            <span>{{ $t('funnel.canvas.editor.footer.tooltip.keyboard.shortcuts') }}</span>
                        </template>
                    </DsTooltip>

                    <DsTooltip>
                        <template #reference>
                            <DsIconButton
                                data-qa="note-button"
                                draggable
                                class="overlay-btn"
                                name="edit"
                                @click="onClickNoteButton"
                            />
                        </template>
                        <template #default>
                            <span>{{ $t('funnel.canvas.editor.footer.tooltip.notes') }}</span>
                        </template>
                    </DsTooltip>

                    <DsTooltip v-if="flagsInfo.showFindElementEnabled">
                        <template #reference>
                            <DsIconButton
                                class="overlay-btn"
                                name="search"
                                @click="$emit('open-element-list-sidebar')"
                            />
                        </template>

                        <template #default>
                            <span>{{ $t('funnel.canvas.editor.footer.find') }}</span>
                        </template>
                    </DsTooltip>

                    <DsTooltip>
                        <template #reference>
                            <DsIconButton
                                class="overlay-btn"
                                name="target"
                                data-qa="recenter-canvas"
                                @click="onCenterCanvas"
                            />
                        </template>

                        <template #default>
                            <span>{{ $t('funnel.canvas.editor.footer.recenter.canvas') }}</span>
                        </template>
                    </DsTooltip>
                </template>

                <DsIconButton
                    v-if="debugMode"
                    class="overlay-btn"
                    name="search"
                    @click="openCellSearch"
                />

                <div v-if="!showFirstTimeUse" class="campaign-zoom-container">
                    <DsTooltip>
                        <template #reference>
                            <DsButton class="zoom-btn" name="minus" @click="zoomOut">
                                <img src="/resources/funnel/images/svg/cell-icon/zoom-out.svg" />
                            </DsButton>
                        </template>
                        <template #default>
                            <span>{{ $t('funnel.canvas.editor.footer.zoom.out') }}</span>
                        </template>
                    </DsTooltip>

                    <span class="zoom-label">{{ Math.round((internalZoomLevel - 0.5) * 100) }}%</span>

                    <DsTooltip>
                        <template #reference>
                            <DsButton class="zoom-btn" name="add" @click="zoomIn">
                                <img src="/resources/funnel/images/svg/cell-icon/zoom-in.svg" />
                            </DsButton>
                        </template>
                        <template #default>
                            <span>{{ $t('funnel.canvas.editor.footer.zoom.in') }}</span>
                        </template>
                    </DsTooltip>
                </div>
                <div v-if="!isSequence && !showReportingView" class="overlay-btn feedback-button">
                    <DsOutlineButton
                        data-qa="feedback-button"
                        @click="openFeedbackModal"
                    >
                        <template #default>
                            {{ $t('funnel.canvas.editor.footer.feedback.text') }}
                        </template>
                        <template #trailing>
                            <DsIcon name="maximize" />
                        </template>
                    </DsOutlineButton>
                </div>
            </div>
        </div>

        <DsInputField
            v-show="editableNameData && editableNameData.cell.type !== $options.NOTES_TYPE"
            ref="editableTextInputOverlay"
            v-model="editableName"
            tabindex="3"
            class="editable-text-overlay"
            data-qa="editable-text-overlay"
            :style="editableNameData && editableNameData.position"
            :placeholder="$t('funnel.canvas.editor.inline.cell.rename.placeholder')"
            @blur="saveCellName"
        />

        <textarea
            v-show="isEditingNote && !notesWithToolbarFlag"
            :key="editableNameData && editableNameData.cell.meta.id"
            ref="editableTextAreaOverlay"
            v-model="editableName"
            tabindex="4"
            class="editable-text-area-overlay"
            :style="editableNameData && editableNameData.position"
            :placeholder="$t('funnel.edit.notes.default')"
            @blur="saveNoteValue"
            @keyup.enter="onEnterNote"
            @change="updateCellName"
        />

        <QuillEditor
            v-if="isEditingNote && notesWithToolbarFlag"
            ref="quillEditorForNotes"
            class="quill-editor-for-notes"
            :selected-color="notesBackgroundColor"
            :note-content="editableNameData"
            :style="Object.assign({}, editableNameData && editableNameData.position, { backgroundColor: (editableNameData && editableNameData.notesColor) || $options.NOTE_COLOR.gray })"
            @change-note-color="changeNotesColor"
            @blur="saveNoteValue"
            @keyup.enter="onEnterNote"
            @change="updateCellName"
        />
    </div>
</template>

<script>
import { findChildren, isParentCellLocked } from '../utils/cell/automation-cell-utils';
import { validatePoint } from '../utils/cell/automation-mapper-utils';
import { getBigCardElementHeight, setCursor } from '../utils/cell/automation-display-utils';
import { decodeHtml } from '../../../util/marketing/site.utils';
import { isEdgeValid } from '../utils/cell/automation-edge-utils';
import KonvaAutomationInlineAdd from './KonvaAutomationInlineAdd.vue';
import KonvaCampaignSidebar from './KonvaCampaignSidebar.vue';
import KonvaCampaignCell from './KonvaCampaignCell.vue';
import KonvaDraggedCell from './KonvaDraggedCell.vue';
import KonvaCampaignImage from './KonvaCampaignImage.vue';
import KonvaReportingFilterButton from '../reporting/KonvaReportingFilterButton.vue';
import ReportingCard from '../reporting/ReportingCard.vue';
import EmailPerformanceCard from '../reporting/EmailPerformanceCard.vue';

import {
    BIG_CARD_WIDTH,
    CELL_TYPE,
    EDGE_TYPE,
    EDGE_VALIDATION_INVALID_STATES,
    EDGE_VALIDATION_STATE,
    GRID_SIZE,
    INTEGRATION_ACTION_TYPE,
    INTEGRATION_TRIGGER_TYPE,
    MAIN_CANVAS_ID,
    NOTE_COLOR,
    NOTE_HEIGHT,
    NOTE_WIDTH,
    NOTES_TYPE,
    PRESET_FILTERS,
    PROCESS_APPLY_REMOVE_TAG,
    PROCESS_EMAIL_CONFIRM,
    SEQUENCE_START,
    SEQUENCE_START_WIDTH,
} from '../utils/editor.constants';
import QuillEditor from '../components/QuillEditor.vue';
import { cleanNote } from '../utils/notes-utils';

const COLOR = {
    PURPLE: 'purple',
    RED: 'red',
    BLACK: 'black',
};

const CONTROL_POINT_CURVE_RATIO = 100;
const MAX_ZOOM_SCALE = 1.5;
const MIN_ZOOM_SCALE = 0.7;
const SCALE_BY = 0.1;
const DEFAULT_ZOOM_LEVEL = 1;
const MINIMUM_ZOOM_TO_SHOW_BIG_CARDS = 1.5;
const MINIMUM_ZOOM_TO_SHOW_LABELS = 0.7;
const KEY_SPACE = ' ';
const KEY_A = 'a';
const KEY_B = 'b';
const KEY_J = 'j';
const KEY_SLASH = '/';
const KEY_ENTER = 'Enter';
const KEY_SHIFT = 'Shift';
const KEY_ESCAPE = 'Escape';
const KEY_ARROW_UP = 'ArrowUp';
const KEY_ARROW_DOWN = 'ArrowDown';
const KEY_ARROW_LEFT = 'ArrowLeft';
const KEY_ARROW_RIGHT = 'ArrowRight';
const KEY_BACKSPACE = 'Backspace';
const KEY_DELETE = 'Delete';
const KEY_QUESTION = '?';
const KEY_EQUAL = '=';
const KEY_PLUS = '+';
const KEY_MINUS = '-';

const DEBUG_CODE_DEFAULT = [
    KEY_ARROW_UP,
    KEY_ARROW_UP,
    KEY_ARROW_DOWN,
    KEY_ARROW_DOWN,
    KEY_ARROW_LEFT,
    KEY_ARROW_RIGHT,
    KEY_ARROW_LEFT,
    KEY_ARROW_RIGHT,
    KEY_B,
    KEY_A,
    KEY_ENTER,
];

export default {
    NOTES_TYPE,
    NOTE_COLOR,
    EDGE_VALIDATION_STATE,
    components: {
        QuillEditor,
        ReportingCard,
        EmailPerformanceCard,
        KonvaAutomationInlineAdd,
        KonvaCampaignSidebar,
        KonvaCampaignCell,
        KonvaDraggedCell,
        KonvaReportingFilterButton,
        KonvaCampaignImage,
    },
    props: {
        displayWidgets: {
            type: Boolean,
            default: true,
        },
        flagsInfo: {
            type: Object,
            default: () => ({}),
        },
        debugMode: {
            type: Boolean,
            default: false,
        },
        enabledCellTypes: {
            type: Array,
            required: true,
        },
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
        isInlineAddOpen: {
            type: Boolean,
            default: false,
        },
        reportData: {
            type: Object,
            default: () => ({}),
        },
        emailStats: {
            type: Object,
            default: () => ({}),
        },
        uniqueContacts: {
            type: Number,
            default: 0,
        },
        preset: {
            type: String,
            default: PRESET_FILTERS.LAST_7_DAYS,
        },
        showHotkeys: {
            type: Boolean,
            default: false,
        },
        showSidebar: {
            type: Boolean,
            default: false,
        },
        editableNameData: {
            type: Object,
            default: () => {},
        },
        selectedSequence: {
            type: String,
            required: true,
        },
        showReportingView: {
            type: Boolean,
            default: false,
        },
        cells: {
            type: Array,
            default: () => ([]),
        },
        publishedCells: {
            type: Array,
            required: true,
        },
        selectedCells: {
            type: Array,
            default: () => ([]),
        },
        showConfigModal: {
            type: Boolean,
            default: false,
        },
        isSequence: {
            type: Boolean,
            default: false,
        },
        betaFeaturesEnabled: {
            type: Boolean,
            required: true,
        },
        initialZoomLevel: {
            type: Number,
            required: true,
        },
        rightOverlayWidth: {
            type: Number,
            default: 0,
        },
    },

    emits: [
        'add-cell',
        'click-cell',
        'click-stage',
        'close-sequence',
        'copy-cells',
        'delete-cells',
        'enable-debug-mode',
        'move-cells',
        'on-open-filter-modal',
        'open-email-performance-report',
        'open-sequence-email-report',
        'open-automation-feedback-modal',
        'open-report',
        'open-config-modal',
        'open-element-list',
        'open-element-list-sidebar',
        'open-inline-add',
        'open-between-add',
        'close-inline-add',
        'rename-cell',
        'resize',
        'set-editable-name-data',
        'set-selected-cells',
        'toggle-hotkeys',
        'update-note',
        'update-note-color',
        'update-initial-zoom-level',
    ],

    data() {
        return {
            showClickToOpen: false,
            debugModeCode: DEBUG_CODE_DEFAULT,
            lastKeyPressed: null,
            isPanning: false,
            mousePos: {},
            attachingEdgeFrom: null,
            selectBoxBounds: null,
            pageOffset: { x: 0, y: 0 },
            holdingShift: false,
            hoveredCellId: null,
            dragOffset: { x: 0, y: 0 },
            isCopying: false,
            stagePan: { x: 0, y: 0 },
            draggedCellIds: [],
            draggedOverCell: null,
            internalZoomLevel: this.initialZoomLevel,
            containerWidth: 750,
            containerHeight: 600,
            isSidebarCollapsed: false,
            noteOffset: 50,
        };
    },

    computed: {
        notesBackgroundColor() {
            return this.editableNameData.notesColor ?? NOTE_COLOR.gray;
        },
        clickToOpenRatioEmails() {
            return `${this.emailStats.clickToOpenRatio}%`;
        },

        editableName: {
            get() {
                return this.editableNameData?.name || '';
            },
            set(value) {
                this.setEditableNameData({ ...this.editableNameData, name: value });
            },
        },

        notesWithToolbarFlag() {
            return this.flagsInfo.addToolbarToNotesEnabled;
        },

        isEditingNote() {
            if (!this.isSequence && this.editableNameData?.cell.meta.parent !== this.selectedSequence) return false;

            return this.editableNameData?.cell.type === NOTES_TYPE;
        },

        showFirstTimeUse() {
            return (!this.isSequence && !this.displayCells.length && !this.showReportingView);
        },

        showInlineAdd() {
            return this.bigCardInlineAddEnabled
                && this.validInlineAddSources.length
                && !this.showReportingView
                && !this.draggedCellIds?.length
                && !this.attachingEdgeFrom
                && !this.isEmailConfirmation;
        },

        shouldShowLabels() {
            return this.internalZoomLevel > MINIMUM_ZOOM_TO_SHOW_LABELS;
        },

        shouldShowBigCard() {
            if (!this.bigCardInlineAddEnabled || this.showReportingView) return false;

            return this.internalZoomLevel >= MINIMUM_ZOOM_TO_SHOW_BIG_CARDS;
        },

        validInlineAddSources() {
            const sourceIdsFromEdges = this.cells
                .filter((cell) => cell.type === EDGE_TYPE)
                .map((cell) => cell.meta.source);

            const validCells = this.displayCells.filter((cell) => {
                const isInvalidSourceType = [EDGE_TYPE, NOTES_TYPE].includes(cell.type);
                const isSameSequence = cell.meta.parent === this.selectedSequence;
                const hasOutgoingElements = sourceIdsFromEdges.includes(cell.meta.id);

                if (isInvalidSourceType || !isSameSequence || hasOutgoingElements) return false;

                const existingElementBlockingInlineAdd = this.displayCells.find((existingCell) => {
                    return existingCell.meta.id !== cell.meta.id
                        && existingCell.mxGeometry.y === cell.mxGeometry.y
                        && existingCell.mxGeometry.x >= cell.mxGeometry.x
                        && existingCell.mxGeometry.x <= cell.mxGeometry.x + GRID_SIZE * 6;
                });

                return !existingElementBlockingInlineAdd;
            });

            if (!this.addElementsBetweenEnabled) return validCells;

            const highestIdCell = validCells.reduce((maxCell, cell) => (
                Number(maxCell.meta.id) > Number(cell.meta.id) ? maxCell : cell
            ), validCells[0]);

            return highestIdCell ? [highestIdCell] : [];
        },

        draggedEdgeValidState() {
            if (!this.draggedOverCell || !this.attachingEdgeFrom) return EDGE_VALIDATION_STATE.NONE;

            return isEdgeValid(this.attachingEdgeFrom, this.draggedOverCell.meta.id, this.cells);
        },

        isDragging() {
            return this.draggedCellIds.length > 0;
        },

        draggedCells() {
            return this.cellsWithChildren.filter((cell) => this.draggedCellIds.includes(cell.meta.id) && cell.type !== EDGE_TYPE);
        },

        shouldShowGrid() {
            const MINIMUM_ZOOM_TO_SHOW_GRID = 2.5;

            return this.internalZoomLevel >= MINIMUM_ZOOM_TO_SHOW_GRID;
        },

        cellsWithChildren() {
            return this.cells.map((cell) => {
                const children = findChildren(cell.meta.id, this.cells);
                const elementChildren = children.filter((child) => ![EDGE_TYPE, SEQUENCE_START].includes(child.type));

                return { ...cell, children, elementChildren };
            });
        },

        stageStyleOverride() {
            if (!this.shouldShowGrid) return '';

            return {
                'background-size': `${GRID_SIZE * 2 * this.internalZoomLevel}px`,
                'background-position-x': `${this.stagePan.x}px`,
                'background-position-y': `${this.stagePan.y}px`,
            };
        },

        showHorizontalGuideline() {
            const {
                mousePos, pageOffset, stagePan, internalZoomLevel, draggedCellIds, displayCells,
            } = this;

            if (!draggedCellIds.length) return false;

            const mouseCanvasY = (mousePos.y - pageOffset.y - stagePan.y) / internalZoomLevel;
            const snapToGridMouseY = Math.floor(mouseCanvasY / GRID_SIZE) * GRID_SIZE;

            return !!displayCells.find((cell) => {
                return cell.mxGeometry.y === snapToGridMouseY && !draggedCellIds.includes(cell.id);
            });
        },

        showVerticalGuideline() {
            const {
                mousePos, pageOffset, stagePan, internalZoomLevel, draggedCellIds, displayCells,
            } = this;

            if (!draggedCellIds.length) return false;

            const mouseCanvasX = (mousePos.x - pageOffset.x - stagePan.x) / internalZoomLevel;
            const snapToGridMouseX = Math.floor(mouseCanvasX / GRID_SIZE) * GRID_SIZE;

            return !!displayCells.find((cell) => {
                return cell.mxGeometry.x === snapToGridMouseX && !draggedCellIds.includes(cell.meta.id);
            });
        },

        configDebugmodeContentHoveredCellText() {
            if (!this.debugMode) return {};

            const hoveredCell = this.displayCells.find((displayedCell) => displayedCell.meta.id === this.hoveredCellId);
            const hoveredCellText = (JSON.stringify(hoveredCell, null, 4) || '');

            return {
                x: 0 - this.stagePan.x,
                y: 0 - this.stagePan.y,
                text: `${hoveredCellText}`,
                width: 400,
                draggable: false,
                align: 'left',
                opacity: 1,
                fontSize: 10,
                fontFamily: 'Proxima Nova',
                fill: 'black',
            };
        },

        configDragGuideline() {
            const { pageOffset, stagePan } = this;

            const viewPortCoords = {
                x1: (0 - stagePan.x) / this.internalZoomLevel,
                y1: (0 - stagePan.y) / this.internalZoomLevel,
                x2: (pageOffset.width - stagePan.x) / this.internalZoomLevel,
                y2: (pageOffset.height - stagePan.y) / this.internalZoomLevel,
            };

            const mouseCanvasX = (this.mousePos.x - pageOffset.x - stagePan.x) / this.internalZoomLevel;
            const mouseCanvasY = (this.mousePos.y - pageOffset.y - stagePan.y) / this.internalZoomLevel;

            const snapToGridMouseX = Math.floor(mouseCanvasX / GRID_SIZE) * GRID_SIZE;
            const snapToGridMouseY = Math.floor(mouseCanvasY / GRID_SIZE) * GRID_SIZE;

            return {
                vertical: {
                    dash: [5, 3],
                    dashEnabled: true,
                    stroke: 'blue',
                    strokeWidth: 1,
                    opacity: 0.5,
                    tension: 1,
                    x: 0,
                    y: 0,
                    points: [snapToGridMouseX, viewPortCoords.y1, snapToGridMouseX, viewPortCoords.y2],
                },

                horizontal: {
                    dash: [5, 3],
                    dashEnabled: true,
                    stroke: 'blue',
                    strokeWidth: 1,
                    opacity: 0.5,
                    tension: 1,
                    x: 0,
                    y: 0,
                    points: [viewPortCoords.x1, snapToGridMouseY, viewPortCoords.x2, snapToGridMouseY],
                },
            };
        },

        configContainer() {
            return {
                x: this.showReportingView ? 400 : 0,
                y: 0,
                scaleX: this.effectiveZoomLevel,
                scaleY: this.effectiveZoomLevel,
                width: this.containerWidth,
                height: this.containerHeight,
                draggable: !this.showFirstTimeUse && !this.holdingShift,
            };
        },

        configAttachingEdge() {
            if (!this.attachingEdgeFrom) return null;

            const fromCell = this.displayCells.find((cell) => {
                return cell.id === this.attachingEdgeFrom;
            });

            if (!fromCell) return {};

            const mouseCanvasX = (((this.mousePos.x - 12) - this.pageOffset.x - this.stagePan.x) / this.internalZoomLevel);
            const mouseCanvasY = ((this.mousePos.y + 5) - this.pageOffset.y - this.stagePan.y) / this.internalZoomLevel;

            let fromCellX = fromCell.mxGeometry.x + (GRID_SIZE / 2);
            const fromCellY = fromCell.mxGeometry.y + (GRID_SIZE / 2);

            if (this.shouldShowBigCard) {
                fromCellX = fromCell.mxGeometry.x + BIG_CARD_WIDTH;
            }

            const cp1x = fromCellX + CONTROL_POINT_CURVE_RATIO;
            const cp1y = fromCellY;
            const cp2x = mouseCanvasX - CONTROL_POINT_CURVE_RATIO;
            const points = [fromCellX, fromCellY, cp1x, cp1y, cp2x, mouseCanvasY, mouseCanvasX, mouseCanvasY];

            const attachEdgeColor = EDGE_VALIDATION_INVALID_STATES.includes(this.draggedEdgeValidState)
                ? COLOR.RED
                : COLOR.BLACK;

            const { color, pointerImage } = this.getPointerColorAndImage(attachEdgeColor);

            return {
                pointerImage,
                pointerConfig: {
                    x: mouseCanvasX,
                    y: mouseCanvasY - 16,
                },
                arrowConfig: {
                    fill: color,
                    stroke: color,
                    strokeWidth: 2,
                    pointerAtEnding: false,
                    bezier: true,
                    points,
                },
            };
        },

        configFirstTimeUseContainer() {
            const width = this.isSidebarCollapsed ? 100 : 450;

            return {
                x: (this.containerWidth / 2 / this.effectiveZoomLevel) - (width / 2),
                y: Math.max((this.containerHeight / 2 / this.effectiveZoomLevel) - 150, 50),
                width,
                height: this.containerHeight,
                draggable: false,
            };
        },

        configFirstTimeUse() {
            return {
                text: this.$t('funnel.canvas.editor.first.time.use.header'),
                draggable: false,
                align: 'center',
                fontSize: 32,
                fontStyle: 'bold',
                fontFamily: 'Poppins Bold',
            };
        },


        configFirstTimeUseSubheader() {
            return {
                x: 50,
                y: 50,
                text: this.bigCardInlineAddEnabled
                    ? this.$t('funnel.canvas.editor.first.time.use.subheader')
                    : this.$t('funnel.canvas.editor.first.time.use.subheader.no.inlineadd'),
                draggable: false,
                width: 333,
                align: 'center',
                fontSize: 16,
                fontFamily: 'Sul Sans',
                fill: '#666',
            };
        },

        showFirstTimeUseImg() {
            return !this.bigCardInlineAddEnabled;
        },

        configFirstTimeUseImg() {
            return {
                config: {
                    x: 145,
                    y: 110,
                    width: 144,
                    height: 144,
                },
                src: '/resources/png/custom-campaign.png',
            };
        },

        configSelectBoxCorners() {
            const cornerStyle = {
                width: 10,
                height: 10,
                fill: 'white',
                stroke: this.getPointerColorAndImage(COLOR.PURPLE).color,
                strokeWidth: 2,
            };

            return [{
                ...cornerStyle,
                index: 'corner-1',
                x: this.selectBoxBounds[0].x - 5,
                y: this.selectBoxBounds[0].y - 5,
            }, {
                ...cornerStyle,
                index: 'corner-2',
                x: this.selectBoxBounds[1].x - 5,
                y: this.selectBoxBounds[0].y - 5,
            }, {
                ...cornerStyle,
                index: 'corner-3',
                x: this.selectBoxBounds[0].x - 5,
                y: this.selectBoxBounds[1].y - 5,
            }, {
                ...cornerStyle,
                index: 'corner-4',
                x: this.selectBoxBounds[1].x - 5,
                y: this.selectBoxBounds[1].y - 5,
            }];
        },

        configSelectBox() {
            const [selectStartPoint, selectEndPoint] = this.selectBoxBounds || [];

            return {
                stroke: this.getPointerColorAndImage(COLOR.PURPLE).color,
                strokeWidth: 2,
                x: selectStartPoint?.x,
                y: selectStartPoint?.y,
                width: (selectEndPoint?.x || 0) - (selectStartPoint?.x || 0),
                height: (selectEndPoint?.y || 0) - (selectStartPoint?.y || 0),
            };
        },

        hoveredCell() {
            return this.displayCells.find((displayedCell) => {
                return this.hoveredCellId === displayedCell.meta.id;
            });
        },

        displayedEdges() {
            const edges = this.cells.filter((cell) => {
                return cell.meta.parent === this.selectedSequence
                    && cell.type === EDGE_TYPE;
            });

            return edges.map((edge) => {
                const source = this.cells.find((cell) => cell.meta.id === edge.meta.source);
                const target = this.cells.find((cell) => cell.meta.id === edge.meta.target);

                if (!source || !target) return null;
                source.mxGeometry = validatePoint(source.mxGeometry);
                target.mxGeometry = validatePoint(target.mxGeometry);

                const sourcePoint = {
                    x: source.mxGeometry.x + GRID_SIZE,
                    y: source.mxGeometry.y + (GRID_SIZE / 2),
                };

                if (this.shouldShowBigCard) {
                    if (source.type === SEQUENCE_START) {
                        sourcePoint.x = source.mxGeometry.x + BIG_CARD_WIDTH - SEQUENCE_START_WIDTH;
                    } else {
                        sourcePoint.x = source.mxGeometry.x + BIG_CARD_WIDTH;
                    }
                }

                const targetPoint = {
                    x: target.mxGeometry.x - 34,
                    y: target.mxGeometry.y + (GRID_SIZE / 2),
                };

                const cp1x = sourcePoint.x + CONTROL_POINT_CURVE_RATIO;
                const cp1y = sourcePoint.y;
                const cp2x = targetPoint.x - CONTROL_POINT_CURVE_RATIO;
                const cp2y = targetPoint.y;
                const points = [sourcePoint.x, sourcePoint.y, cp1x, cp1y, cp2x, cp2y, targetPoint.x, targetPoint.y];

                const isSelected = !!this.selectedCells.find((cell) => cell.meta.id === edge.meta.id);

                const { color, pointerImage } = this.getPointerColorAndImage(isSelected ? COLOR.PURPLE : COLOR.GRAY);

                return {
                    element: edge,
                    pointerImage,
                    pointerConfig: { x: targetPoint.x, y: targetPoint.y - 16 },
                    arrowConfig: {
                        id: edge.meta.id,
                        fill: color,
                        stroke: color,
                        strokeWidth: 2,
                        borderRadius: 20,
                        pointerAtEnding: false,
                        bezier: true,
                        hitStrokeWidth: 15,
                        points,
                    },
                };
            }).filter((c) => !!c);
        },

        displayCells() {
            return this.cellsWithChildren.filter((cell) => {
                return cell.meta.parent === this.selectedSequence
                        && cell.type !== EDGE_TYPE;
            }).map((cell) => ({
                ...cell,
                isBeingDragged: this.draggedCellIds.includes(cell.meta.id),
            }));
        },

        bigCardInlineAddEnabled() {
            return this.flagsInfo.bigCardInlineAdd && this.betaFeaturesEnabled;
        },

        effectiveZoomLevel() {
            if (this.showFirstTimeUse) {
                return MAX_ZOOM_SCALE;
            }

            return this.internalZoomLevel;
        },

        isEmailConfirmation() {
            return this.isSequence && this.displayCells?.length > 2 && this.displayCells[1].type === PROCESS_EMAIL_CONFIRM;
        },

        addElementsBetweenEnabled() {
            return this.flagsInfo.addElementsBetweenEnabled && this.bigCardInlineAddEnabled;
        },

        effectiveRightOverlayWidth() {
            return this.isSequence ? 0 : (this.rightOverlayWidth || 0);
        },

        visibleWidthPx() {
            return Math.max(1, this.containerWidth - this.effectiveRightOverlayWidth);
        },
    },

    watch: {
        isEditingNote: {
            handler(value, oldValue) {
                if (value && !oldValue) {
                    this.$nextTick(() => {
                        if (!this.notesWithToolbarFlag) {
                            this.$refs.editableTextAreaOverlay.focus();
                        }
                    });
                }
            },
        },

        showConfigModal: {
            async handler(value) {
                if (!value) {
                    await this.$nextTick();
                    this.resizeCanvas();
                    this.$refs.stage.$el.focus();
                }
            },
        },

        displayCells: {
            handler(displayCells) {
                if (!displayCells.length) {
                    this.onCenterCanvas();
                }
            },
        },

        reportData: {
            handler() {
                this.resizeCanvas();
            },
        },

        initialZoomLevel(newZoom) {
            this.internalZoomLevel = newZoom;
        },

        internalZoomLevel(newZoom) {
            if (!this.isSequence) {
                this.$emit('update-initial-zoom-level', newZoom);
            }
        },

        showSidebar(val, oldVal) {
            if (val && !oldVal) {
                this.isSidebarCollapsed = false;
            }
        },
    },

    mounted() {
        // eslint-disable-next-line no-underscore-dangle
        window.Konva._fixTextRendering = true;
        this.addListeners();
        this.resizeCanvas();
    },

    beforeUnmount() {
        this.removeListeners();
    },

    methods: {
        openClickToOpen() {
            this.showClickToOpen = true;
        },

        closeClickToOpen() {
            this.showClickToOpen = false;
        },

        setEditableNameData(editableNameData) {
            this.$emit('set-editable-name-data', editableNameData);
        },

        getPointerColorAndImage(color) {
            if (color === COLOR.PURPLE) {
                return {
                    color: '#8358f1',
                    pointerImage: '/resources/funnel/images/svg/connectors/purple.svg',
                };
            }

            if (color === COLOR.RED) {
                return {
                    color: '#FF0000',
                    pointerImage: '/resources/funnel/images/svg/connectors/red.svg',
                };
            }

            if (color === COLOR.BLACK) {
                return {
                    color: '#000',
                    pointerImage: '/resources/funnel/images/svg/connectors/default.svg',
                };
            }

            return {
                color: '#ddd',
                pointerImage: '/resources/funnel/images/svg/connectors/gray.svg',
            };
        },

        onOpenInlineAddMenu(inlineAddData) {
            const { stagePan, internalZoomLevel } = this;

            this.$emit('open-inline-add', {
                ...inlineAddData,
                stagePan,
                internalZoomLevel,
            });
        },

        onOpenBetweenAdd(betweenAddData) {
            const { stagePan, internalZoomLevel } = this;

            this.$emit('open-between-add', {
                ...betweenAddData,
                stagePan,
                internalZoomLevel,
            });
        },

        onCloseInlineAddMenu() {
            this.$emit('close-inline-add');
        },

        onClickNoteButton() {
            const rect = this.$refs.stage.$el.getBoundingClientRect();
            const { bottom } = rect;

            const startX = (GRID_SIZE - this.stagePan.x) / this.internalZoomLevel;
            const startY = ((Math.floor(bottom / GRID_SIZE) * GRID_SIZE) - 200 - this.stagePan.y) / this.internalZoomLevel;

            let newX = startX;
            let newY = startY;

            while (this.isPositionOccupied(newX, newY)) {
                newX += this.noteOffset;
                newY -= this.noteOffset;
            }

            this.$emit('add-cell', {
                cellName: NOTES_TYPE,
                parent: this.selectedSequence,
                autoSelectCell: false,
                width: this.notesWithToolbarFlag ? NOTE_WIDTH : 200,
                height: this.notesWithToolbarFlag ? NOTE_HEIGHT : 100,
                x: newX,
                y: newY,
            });
        },

        isPositionOccupied(x, y) {
            return this.displayCells.some((cell) => cell.type === NOTES_TYPE && cell.mxGeometry.x === x && cell.mxGeometry.y === y);
        },

        getCellValidState(cell) {
            if (cell.meta.id !== this.draggedOverCell?.meta?.id) return EDGE_VALIDATION_STATE.NONE;

            return this.draggedEdgeValidState;
        },

        cellHasBeenPublished(cell) {
            return !!this.publishedCells.find((publishedCell) => publishedCell.id === cell.id);
        },

        positionCanvas({ x, y }) {
            this.$refs.stage.getNode().position({ x, y });
            this.resizeCanvas();
        },

        onCenterCanvas() {
            this.internalZoomLevel = DEFAULT_ZOOM_LEVEL;
            this.positionCanvas({ x: 0, y: 0 });
        },

        onUpdateMousePos(event) {
            this.mousePos = { x: event.x, y: event.y };
        },

        updateCellName(event) {
            this.editableName = event.target.value;
        },

        changeNotesColor({ color }) {
            this.setEditableNameData({
                ...this.editableNameData,
                notesColor: color,
            });
            const { cell } = this.editableNameData;

            this.$emit('update-note-color', { cell, color });
        },

        saveNoteValue() {
            if (!this.editableNameData) return;

            const { cell } = this.editableNameData;
            const editableTextAreaElement = this.notesWithToolbarFlag ? document.querySelector('.quill-editor-for-notes') : this.$refs.editableTextAreaOverlay;
            const name = this.notesWithToolbarFlag ? (decodeHtml(document.querySelector('.ql-editor').innerHTML) || '') : (editableTextAreaElement?.value || '');
            const { clientWidth, clientHeight } = editableTextAreaElement;

            const mxGeometry = {
                height: clientHeight || cell.mxGeometry.height,
                width: clientWidth || cell.mxGeometry.width,
            };

            if (this.flagsInfo.imagesWithCorsIssueFixEnabled) {
                const sanitizedNote = cleanNote(name);

                this.$emit('update-note', { cell, name: sanitizedNote, mxGeometry });
            } else {
                this.$emit('update-note', { cell, name, mxGeometry });
            }

            this.setEditableNameData(null);
        },

        saveCellName() {
            if (!this.editableNameData) return;

            const { cell } = this.editableNameData;

            if (cell.type === NOTES_TYPE) {
                this.saveNoteValue();
            } else {
                const name = this.editableName?.trim();

                if (name?.length) this.$emit('rename-cell', { cell, name });
            }
            this.setEditableNameData(null);
        },

        onEnterNote(event) {
            if (event.shiftKey) return;

            event.target.blur();
        },

        onClickName({ event, cell }) {
            const isApplyOrRemoveTag = cell.type === PROCESS_APPLY_REMOVE_TAG;

            if (this.isPanning || isApplyOrRemoveTag || this.showReportingView || isParentCellLocked(cell, this.cells)) return;

            const textPosition = event.target ? event.target.getClientRect() : event.event.target.getClientRect();

            const stageElement = this.$refs.stage?.$el;
            const stagePosition = stageElement?.getBoundingClientRect();

            if (!stagePosition) return;

            const editableNameElement = this.$refs.editableTextInputOverlay.$el.firstChild;

            this.setEditableNameData({
                cell,
                name: cell.value.name,
                position: {
                    left: `${stagePosition.left + textPosition.x}px`,
                    top: `${stagePosition.top + textPosition.y}px`,
                    width: `${textPosition.width}px`,
                    transform: `scale(${this.internalZoomLevel})`,
                },
            });

            this.$nextTick(() => {
                document.activeElement?.blur();
                editableNameElement?.focus();
            });
        },

        onDragEdge({ cell }) {
            this.attachingEdgeFrom = cell.id;
        },

        onDropEdge() {
            if (this.draggedEdgeValidState === EDGE_VALIDATION_STATE.VALID) {
                this.$emit('add-cell', {
                    cellName: EDGE_TYPE,
                    target: this.draggedOverCell.meta.id,
                    source: this.attachingEdgeFrom,
                    parent: this.selectedSequence,
                    autoSelectCell: false,
                });
            }

            if (EDGE_VALIDATION_INVALID_STATES.includes(this.draggedEdgeValidState)) {
                this.$toast({ message: this.$t(`funnel.canvas.editor.edge.creation.error.${this.draggedEdgeValidState}`) });
            }
            this.hoveredCellId = null;
            this.attachingEdgeFrom = null;
        },

        onClickStage(event) {
            if (!this.selectBoxBounds && event.target === event.target.getStage()) {
                if (this.editableNameData) {
                    this.saveCellName();
                    setCursor(event, 'default');
                }
                this.attachingEdgeFrom = null;
                this.$emit('click-stage', { event });
            }
        },

        onPanStage() {
            this.$refs.stage.$el.style.cursor = this.isPanning ? 'grab' : 'default';

            this.resizeCanvas();
        },

        onDrag(event) {
            this.$refs.stage.$el.style.cursor = event.evt.ctrlKey ? 'copy' : 'grabbing';

            if (!this.attachingEdgeFrom) return;
            const stage = event.currentTarget;
            const position = stage.getPointerPosition();
            const canvasPosition = {
                x: (position.x - this.stagePan.x) / this.internalZoomLevel,
                y: (position.y - this.stagePan.y) / this.internalZoomLevel,
            };

            const effectiveWidth = this.shouldShowBigCard ? BIG_CARD_WIDTH : GRID_SIZE;

            this.draggedOverCell = this.displayCells.find((cell) => {
                const bigCardCells = cell.elementChildren?.length
                    ? cell.elementChildren
                    : [cell];

                const effectiveHeight = this.shouldShowBigCard
                    ? getBigCardElementHeight(bigCardCells, this.isSequence)
                    : GRID_SIZE;

                return canvasPosition.x > cell.mxGeometry.x
                    && canvasPosition.x < cell.mxGeometry.x + effectiveWidth
                    && canvasPosition.y > cell.mxGeometry.y
                    && canvasPosition.y < cell.mxGeometry.y + effectiveHeight;
            });
        },

        onMouseEnter({ cell, event }) {
            if (this.showReportingView) return;

            setCursor(event, 'pointer');
            this.hoveredCellId = cell.meta.id;
        },

        onMouseExit({ event }) {
            if (this.showReportingView) return;

            setCursor(event, 'default');
            this.hoveredCellId = null;
        },

        onContextMenu(event) {
            event.evt.preventDefault();
        },

        onMouseDown(event) {
            if (this.holdingShift) {
                const newX = (event.evt.x - this.pageOffset.x - this.stagePan.x) / this.internalZoomLevel;
                const newY = (event.evt.y - this.pageOffset.y - this.stagePan.y) / this.internalZoomLevel;

                this.selectBoxBounds = [
                    { x: newX, y: newY },
                    { x: newX, y: newY },
                ];
            }
        },

        onMouseMove(event) {
            if (!this.holdingShift || !this.selectBoxBounds) return;

            const newX = (event.evt.x - this.pageOffset.x - this.stagePan.x) / this.internalZoomLevel;
            const newY = (event.evt.y - this.pageOffset.y - this.stagePan.y) / this.internalZoomLevel;

            this.selectBoxBounds = [
                this.selectBoxBounds[0],
                { x: newX, y: newY },
            ];

            this.checkSelectBoxBounds();
        },

        onMouseUp() {
            this.holdingShift = false;
            this.selectBoxBounds = null;
        },

        checkSelectBoxBounds() {
            const allCells = this.cells.filter((cell) => cell.meta.parent === this.selectedSequence);

            if (!this.selectBoxBounds) return;

            const xCoords = [this.selectBoxBounds[0].x, this.selectBoxBounds[1].x].sort((a, b) => a - b);
            const yCoords = [this.selectBoxBounds[0].y, this.selectBoxBounds[1].y].sort((a, b) => a - b);

            const DRAG_MARGIN = 1;

            if (xCoords[1] - xCoords[0] < DRAG_MARGIN || yCoords[1] - yCoords[0] < DRAG_MARGIN) return;

            const edges = [];
            const selectedCells = allCells.filter((cell) => {
                const { x, y } = cell.mxGeometry;

                if (cell.type === EDGE_TYPE) {
                    edges.push(cell);

                    return false;
                }

                if (x < xCoords[0] || x > xCoords[1]) return false;
                if (y < yCoords[0] || y > yCoords[1]) return false;

                return true;
            });

            edges.forEach((edge) => {
                const sourceIsSelected = !!selectedCells.find((cell) => cell.meta.id === edge.meta.source);
                const edgeIsSelected = !!selectedCells.find((cell) => cell.meta.id === edge.meta.target);

                if (sourceIsSelected && edgeIsSelected) selectedCells.push(edge);
            });

            this.$emit('set-selected-cells', selectedCells);
        },

        onDelete() {
            this.$emit('delete-cells');
        },

        async onDrop(event) {
            const cellType = event.dataTransfer.getData('cellType');

            if (!cellType) return;

            const autoCreateSequence = cellType === CELL_TYPE.THEN && !this.isSequence;

            const defaultCellType = event.dataTransfer.getData('defaultCellType');
            const cellName = event.dataTransfer.getData('cellName');
            const autoCreateEdge = this.getShouldAutoCreateEdge(cellName);

            const cell = {
                cellName,
                cellDefaultName: event.dataTransfer.getData('cellDefaultName'),
                cellDefaultType: defaultCellType.length ? defaultCellType : null,
                parent: this.selectedSequence,
                x: Math.round((event.offsetX - this.stagePan.x) / this.internalZoomLevel),
                y: Math.round((event.offsetY - this.stagePan.y) / this.internalZoomLevel),
                autoCreateSequence,
                autoCreateEdge,
            };

            if (cellName === INTEGRATION_TRIGGER_TYPE) {
                cell.value = { integrationTriggerId: event.dataTransfer.getData('cellIntegrationId') };
            }

            if (cellName === INTEGRATION_ACTION_TYPE) {
                const integrationId = event.dataTransfer.getData('cellIntegrationId');

                cell.value = {
                    integrationActionId: integrationId,
                };
            }

            this.$emit('add-cell', cell);
        },

        onClickCell({ cell, event }) {
            if (this.isPanning) return;

            if (this.editableNameData && cell.type === NOTES_TYPE && event.type === 'click') {
                this.saveNoteValue();
            }

            this.$emit('click-cell', { cell, event });
        },

        onDoubleClickCell(cell) {
            if (this.isPanning || this.showReportingView) return;
            this.$emit('open-config-modal', cell);
        },

        getCellReportingData(cell) {
            return this.reportData[cell.meta.id] || {};
        },

        onDragStart(cell) {
            if (this.selectedCells.map((selectedCell) => selectedCell.meta.id).includes(cell.meta.id)) {
                this.draggedCellIds = this.selectedCells.map((selectedCell) => selectedCell.meta.id);
            } else {
                this.$emit('set-selected-cells', [cell]);
                this.draggedCellIds = [cell.meta.id];
            }
        },

        onDragMove(event, draggedCell) {
            this.isCopying = event.evt?.ctrlKey;

            draggedCell.mxGeometry = validatePoint(draggedCell.mxGeometry);

            if (event.evt?.ctrlKey || (this.flagsInfo.ghostStatesEnabled || this.attachingEdgeFrom !== null)) {
                this.dragOffset = {
                    x: event.target.x() - draggedCell.mxGeometry.x,
                    y: event.target.y() - draggedCell.mxGeometry.y,
                };
                this.dragOffset = validatePoint(this.dragOffset);
            } else {
                const newPosition = {
                    x: Math.round(event.target.x() / GRID_SIZE) * GRID_SIZE,
                    y: Math.round(event.target.y() / GRID_SIZE) * GRID_SIZE,
                };

                this.$emit('move-cells', { draggedCell, newPosition });

                this.draggedCellIds = [];
                this.dragOffset = { x: 0, y: 0 };
            }
        },

        dragEnd(event, draggedCell) {
            let newPosition = {
                x: Math.round(event.target.x() / GRID_SIZE) * GRID_SIZE,
                y: Math.round(event.target.y() / GRID_SIZE) * GRID_SIZE,
            };

            newPosition = validatePoint(newPosition);

            draggedCell.mxGeometry = validatePoint(draggedCell.mxGeometry);

            if (event.evt?.ctrlKey) {
                this.$emit('copy-cells', { draggedCell, newPosition });
            } else if (this.flagsInfo.ghostStatesEnabled) {
                this.$emit('move-cells', { draggedCell, newPosition });
            }

            this.draggedCellIds = [];
            this.dragOffset = { x: 0, y: 0 };
            this.isCopying = false;
        },

        addListeners() {
            window.addEventListener('resize', this.resizeCanvas);
            window.addEventListener('mousemove', this.onUpdateMousePos);

            const layer = this.$refs?.layer;
            // eslint-disable-next-line no-underscore-dangle
            const canvas = layer?.getNode && layer.getNode().getCanvas()?._canvas;

            if (canvas) canvas.setAttribute('data-fullstory-unmask', '');

            this.$refs.stage.$el.focus();
        },

        removeListeners() {
            window.removeEventListener('resize', this.resizeCanvas);
            window.removeEventListener('mousemove', this.onUpdateMousePos);
        },

        handleKeyDown(event) {
            if (this.editableNameData) return;

            if ((event.key === KEY_EQUAL || event.key === KEY_PLUS) && (event.ctrlKey || event.metaKey)) {
                this.zoomIn();
                event.preventDefault();
            }

            if (event.key === KEY_MINUS && (event.ctrlKey || event.metaKey)) {
                this.zoomOut();
                event.preventDefault();
            }

            if ((event.ctrlKey || event.metaKey) && event.key === KEY_J) {
                this.$emit('open-element-list-sidebar');
            }

            if (event.key === KEY_SPACE) {
                if (this.$refs.stage.$el.style.cursor === 'default') {
                    this.$refs.stage.$el.style.cursor = 'grab';
                }
                this.isPanning = true;
            }

            if (event.key === KEY_SHIFT) {
                this.holdingShift = true;
            }

            if (event.key === KEY_DELETE || event.key === KEY_BACKSPACE && event.metaKey) {
                this.onDelete();
            }
        },

        handleKeyUp(event) {
            if (!this.debugModeCode.length || event.key === this.debugModeCode[0]) {
                if (this.debugModeCode.length === 1) {
                    this.$emit('enable-debug-mode');
                    fetch(`https://pokeapi.co/api/v2/pokemon/${Math.floor(Math.random() * 151) + 1}`)
                        .then((response) => response.json())
                        .then((data) => {
                            const audioElement = new Audio(data.cries.legacy);

                            audioElement.volume = 0.1;
                            const sprite = new Image();

                            sprite.src = data.sprites.front_default;
                            sprite.style.position = 'fixed';
                            sprite.style.bottom = '65';
                            sprite.style.right = '50';

                            document.body.appendChild(sprite);

                            audioElement.addEventListener('canplaythrough', () => audioElement.play());
                            this.$toast({ message: `You caught [${data.name[0].toUpperCase() + data.name.slice(1)}]!` });
                        });
                }
                this.debugModeCode.shift();
            } else {
                this.debugModeCode = DEBUG_CODE_DEFAULT;
            }

            if (this.editableNameData && event.key === KEY_ENTER && !event.shiftKey) {
                event.preventDefault();

                if (this.editableNameData.cell.type !== NOTES_TYPE) {
                    this.saveCellName();
                }
            }

            if (this.editableNameData) return;

            if (event.ctrlKey && event.key === KEY_SLASH && this.lastKeyPressed === KEY_SLASH) {
                this.$emit('open-element-list');
            }

            if (event.key === KEY_ESCAPE) {
                if (this.isSequence && this.lastKeyPressed === KEY_ESCAPE) {
                    this.$emit('close-sequence');
                }
                this.setEditableNameData(null);
                this.$emit('set-selected-cells', []);
            }

            if (event.key === KEY_QUESTION && event.shiftKey && !this.editableNameData) {
                this.toggleHotkeys();
            }

            if (event.key === KEY_SPACE) {
                this.$refs.stage.$el.style.cursor = 'default';
                this.isPanning = false;
            }

            if (event.key === KEY_SHIFT) {
                this.checkSelectBoxBounds();
                this.holdingShift = false;
            }

            this.lastKeyPressed = event.key;
        },

        resizeCanvas() {
            if (!this.$refs.stage?.getNode) return;

            const { container, stage } = this.$refs;

            const element = stage?.$el;

            this.stagePan = stage?.getNode().position() || { x: 0, y: 0 };

            this.containerWidth = container ? container.offsetWidth - 6 : 0;
            this.containerHeight = container ? container.offsetHeight - 6 : 0;

            const stageOffsetX = element?.offsetLeft;
            const stageOffsetY = element?.offsetTop;

            const outerModal = element.closest('.modal-dialog');

            const modalOffsetX = outerModal?.offsetLeft || 0;
            const modalOffsetY = outerModal?.offsetTop || 0;

            this.pageOffset = {
                x: stageOffsetX + modalOffsetX,
                y: stageOffsetY + modalOffsetY,
                width: element?.offsetWidth,
                height: element?.offsetHeight,
            };

            this.setEditableNameData(null);
            this.scale = 1;
            this.$emit('resize');
        },

        toggleHotkeys() {
            this.$emit('toggle-hotkeys');
        },

        handleScroll(event) {
            event.evt.preventDefault();
            if (!event.evt?.ctrlKey) return;

            let newZoomLevel = this.internalZoomLevel;
            const MINIMUM_SCROLL_THRESHOLD = 1;
            const scrollAmount = Math.abs(event.evt.wheelDeltaY) === 120
                ? -event.evt.deltaY
                : event.evt.deltaY;

            if (scrollAmount > MINIMUM_SCROLL_THRESHOLD) {
                if (newZoomLevel >= MAX_ZOOM_SCALE) return;

                newZoomLevel = this.internalZoomLevel + SCALE_BY; // zoom in
            } else if (scrollAmount < -MINIMUM_SCROLL_THRESHOLD) {
                if (newZoomLevel <= MIN_ZOOM_SCALE) return;

                newZoomLevel = this.internalZoomLevel - SCALE_BY; // zoom out
            }

            this.internalZoomLevel = newZoomLevel;
            this.resizeCanvas();
        },

        zoomIn() {
            if (this.internalZoomLevel >= MAX_ZOOM_SCALE) return;
            this.internalZoomLevel += SCALE_BY;
            this.resizeCanvas();
        },

        zoomOut() {
            if (this.internalZoomLevel <= MIN_ZOOM_SCALE) return;
            this.internalZoomLevel -= SCALE_BY;
            this.resizeCanvas();
        },

        openCellSearch() {
            // eslint-disable-next-line no-alert
            const cellId = window.prompt('Enter cell ID:');

            if (!cellId) return;

            const cell = this.cells.find((draftCell) => draftCell.meta.id === cellId);

            if (!cell) {
                this.$toast({ message: `Cell id ${cellId} not found` });

                return;
            }

            if (cell.meta.parent !== MAIN_CANVAS_ID) {
                const parentSequence = this.cells.find((draftCell) => draftCell.meta.id === cell.meta.parent);

                this.$emit('open-config-modal', parentSequence);
            }

            this.$emit('set-selected-cells', [cell]);
        },
        openFeedbackModal() {
            this.$emit('open-automation-feedback-modal');
        },

        getShouldAutoCreateEdge(cellName) {
            const startCells = this.displayCells.filter((cell) => {
                return cell.type === SEQUENCE_START;
            });

            return this.isSequence && cellName !== NOTES_TYPE && (!startCells || startCells.length <= 1);
        },
        handleSidebarToggle(isCollapsed) {
            this.isSidebarCollapsed = isCollapsed;
        },
        getCellBounds(cell) {
            const mx = validatePoint(cell.mxGeometry || {});
            const x = Number(mx.x) || 0;
            const y = Number(mx.y) || 0;

            let width = Number(mx.width);
            let height = Number(mx.height);

            if (cell.type === NOTES_TYPE) {
                width = (Number.isFinite(width) && width > 0) ? width : NOTE_WIDTH;
                height = (Number.isFinite(height) && height > 0) ? height : NOTE_HEIGHT;
            } else if (this.shouldShowBigCard) {
                width = (cell.type === SEQUENCE_START)
                    ? (BIG_CARD_WIDTH - SEQUENCE_START_WIDTH)
                    : BIG_CARD_WIDTH;

                const bigCardCells = cell.elementChildren?.length ? cell.elementChildren : [cell];

                height = getBigCardElementHeight(bigCardCells, this.isSequence);
            } else {
                if (!Number.isFinite(width) || width <= 0) width = GRID_SIZE;
                if (!Number.isFinite(height) || height <= 0) height = GRID_SIZE;
            }

            const cx = x + width / 2;
            const cy = y + height / 2;

            return { x, y, width, height, cx, cy };
        },
        centerOnCell(cell) {
            if (!cell) return;
            const { cx, cy } = this.getCellBounds(cell);

            if (!Number.isFinite(cx) || !Number.isFinite(cy)) return;

            const targetScreenX = this.visibleWidthPx / 2;

            const panX = targetScreenX - (cx * this.internalZoomLevel);
            const panY = (this.containerHeight / 2) - (cy * this.internalZoomLevel);

            this.positionCanvas({ x: panX, y: panY });
        },
        centerCellIfOutOfView(cell) {
            const zoom = this.internalZoomLevel || 1;

            const left = (0 - this.stagePan.x) / zoom;
            const right = (this.visibleWidthPx - this.stagePan.x) / zoom;
            const top = (0 - this.stagePan.y) / zoom;
            const bottom = (this.containerHeight - this.stagePan.y) / zoom;

            const { x, y, width, height } = this.getCellBounds(cell);
            const fullyOutside = (x + width) <= left || x >= right || (y + height) <= top || y >= bottom;

            if (fullyOutside) {
                this.centerOnCell(cell);
            }
        },
    },
};
</script>

<style scoped lang="scss">
.card-filter-container {
    display: flex;
    position: absolute;
    cursor: default;
    width: calc(100% - $spacing-500);
    left: $spacing-250;
    padding-top: $spacing-250;
    justify-content: space-between;
}

.reporting-card-popover {
    z-index: 50;
}

.popover-container {
    --popover-width: 450px;
}

.left-side {
    display: flex;
    align-items: flex-start;
    gap: px-to-rem(20);
}

.reporting-cards {
    display: flex;
    flex-direction: column;
    gap: px-to-rem(14);
}

.editable-text-overlay {
    position: fixed;
    --input-text-align: center;
    --input-height: 100%;
    width: auto;
    min-height: px-to-rem(32px);
}

.editable-text-area-overlay {
    position: fixed;
    text-align: left;
    background-color: #fef8e4;
    font-size: 0.813rem;
    min-height: px-to-rem(32px);
    border-radius: px-to-rem(5px);
    font-family: 'Proxima Nova';
    letter-spacing: px-to-rem(0px);
    box-shadow: $elevation-z8;
    resize: both;
    height: 100%;
    line-height: 1.313rem;
    padding: $spacing-125;
    &:focus {
        border: 1px solid $color-gray-500;
    }
    :deep(div.status) {
        display: none;
    }
}

.quill-editor-for-notes {
    position: fixed;
    text-align: left;
    font-size: 0.813rem;
    border-radius: px-to-rem(8px);
    font-family: 'Proxima Nova',sans-serif;
    letter-spacing: px-to-rem(0px);
    box-shadow: $elevation-z8;
    resize: both;
    min-width: px-to-rem(323px);
    min-height: px-to-rem(214px);
    overflow: auto;
    line-height: 1.313rem;
}

.stage-dropzone {
    overflow: hidden;
}
.campaign-stage {
    height: 100%;
}
.campaign-stage.show-sidebar {
    display: grid;
    grid-template-columns: auto 1fr;
}
.campaign-builder-body {
    height: 100%;
    border: 1px solid $color-gray-200;
    background-color: $color-gray-050;
    background-repeat: repeat;
    background-attachment: scroll;
}
.campaign-builder-body.with-grid {
    background-image: url(/resources/funnel/images/svg/grid_map.svg);
}

.campaign-overlay-footer-buttons {
    display: flex;
    gap: $spacing-200;
    position: absolute;
    margin-left: $spacing-300;
    bottom: $spacing-400;
    width: -webkit-fill-available;
    transition: all 0.5s ease-out;
}

.show-sidebar .campaign-overlay-footer-buttons {
    left: 400px;
    transition: all 0.5s ease-out;

    &.collapsed {
        left: 0px;
        animation: slideInLeft 0.5s ease-out forwards;
    }
}

.campaign-zoom-container {
    z-index: 50;
    background: $color-paper;
    border-radius: $spacing-075;
    box-shadow: $elevation-z2;

    display: grid;
    grid-template-columns: 1fr 1fr 1fr auto;
    width: px-to-rem(96px);
    height: px-to-rem(32px);
    text-align: center;
    margin-left: $spacing-200;

    button {
        background-color: transparent;
        padding: 0;
        width: 100%;
        height: 100%;
        img {
            width: px-to-rem(8);
        }
    }
}

.overlay-btn {
    z-index: 50;
    width: 32px;
    height: 32px;
    background: $color-paper;
    border-radius: 8px;
    box-shadow: $elevation-z2;
    padding: 0;

    &:deep svg.icon {
        margin: auto;
        --icon-size: 16px;
    }
}

.feedback-button {
    margin: 0 $spacing-300 0 auto;
    width: fit-content;
    white-space: nowrap;
    display: flex;
    button.outline {
        --button-text-color: rgba(0, 0, 0, 0.825);
        --button-border-color: transparent;
        font-weight: normal;
    }
    button {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
    }
}

.zoom-label {
    margin: auto;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
    font-family: 'Sul Sans';
}

.wrapper-popup {
    padding: $spacing-250;
}

.main-text {
    font-size: px-to-rem(14);
    color: $color-ink-900;
    margin-bottom: $spacing-100;
}

.sub-heading {
    font-size: px-to-rem(14);
    font-weight: bold;
    color: $color-ink-900;
    margin-bottom: $spacing-050;
}

.calculation-list {
    padding-left: $spacing-300;
    font-size: px-to-rem(14);
    color: $color-ink-900;
    list-style-type: disc;
}
.calculation-list li {
    margin-bottom: $spacing-050;
}
</style>
